import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, Send, Mic, Square, Trash2, Lock, Users, Search,
  Loader2, Paperclip, X, Reply, Calendar, FileVideo, FileAudio, Image as ImageIcon, FileDown, Video, SwitchCamera, Camera, Truck, Smile
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import EmojiPicker from 'emoji-picker-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_VIDEO_BYTES = 500 * 1024 * 1024;

const getToken = () => localStorage.getItem('token');
const authHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const detectType = (filename, contentType) => {
  const ct = (contentType || '').toLowerCase();
  if (ct.startsWith('video')) return 'video';
  if (ct.startsWith('audio')) return 'audio';
  if (ct.startsWith('image')) return 'image';
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (['mp4', 'mov', 'webm', 'avi', 'mkv', 'm4v'].includes(ext)) return 'video';
  if (['mp3', 'wav', 'm4a', 'ogg', 'oga', 'aac', 'flac'].includes(ext)) return 'audio';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
  return 'image';
};

const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
const fmtDate = (d) => { try { return new Date(d + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }); } catch { return d; } };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleString('en-GB', { timeZone: 'Africa/Lagos', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }); } catch { return ''; } };

const DailyFarmOperationsReport = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const role = user.role;
  const isSender = ['staff', 'support_staff'].includes(role);
  const isMgmt = ['admin', 'manager'].includes(role);

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Filters
  const [singleDate, setSingleDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [textSearch, setTextSearch] = useState('');

  // New report form
  const [text, setText] = useState('');
  const [visibility, setVisibility] = useState('management');
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [files, setFiles] = useState([]); // {file, type}
  const fileInputRef = useRef(null);

  // Audio recording
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Camera video recording
  const [cameraOpen, setCameraOpen] = useState(false);
  const [videoRecording, setVideoRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [facingMode, setFacingMode] = useState('environment');
  const [switching, setSwitching] = useState(false);
  const videoPreviewRef = useRef(null);
  const videoStreamRef = useRef(null);   // active camera video stream
  const audioStreamRef = useRef(null);   // persistent mic stream (kept across camera flips)
  const videoRecorderRef = useRef(null);
  const videoChunksRef = useRef([]);
  const elapsedTimerRef = useRef(null);
  const canvasRef = useRef(null);        // offscreen canvas used for the recording pipeline
  const rafRef = useRef(null);

  // Replies
  const [replyText, setReplyText] = useState({});
  const [replyingId, setReplyingId] = useState(null);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (singleDate) params.date = singleDate;
      else {
        if (startDate) params.start_date = startDate;
        if (endDate) params.end_date = endDate;
      }
      const res = await axios.get(`${API}/farm-operations-reports`, { ...authHeader(), params });
      setReports(res.data || []);
      try {
        await axios.post(`${API}/farm-operations-reports-mark-seen`, {}, authHeader());
        window.dispatchEvent(new Event('farmOpsSeen'));
      } catch (_) { /* ignore */ }
    } catch (e) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [singleDate, startDate, endDate]);

  useEffect(() => { loadReports(); }, [loadReports]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList);
    const valid = [];
    for (const f of incoming) {
      const type = detectType(f.name, f.type);
      if (type === 'video' && f.size > MAX_VIDEO_BYTES) {
        toast.error(`"${f.name}" exceeds the 500 MB video limit`);
        continue;
      }
      valid.push({ file: f, type });
    }
    setFiles((prev) => [...prev, ...valid]);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: 'audio/webm' });
        setFiles((prev) => [...prev, { file, type: 'audio' }]);
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error('Microphone access denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const attachVideoStream = (stream) => {
    if (videoPreviewRef.current) {
      videoPreviewRef.current.srcObject = stream;
      videoPreviewRef.current.play().catch(() => {});
    }
  };

  const openCamera = async (mode = 'environment') => {
    try {
      // Persistent mic stream (kept across camera flips so audio stays continuous)
      const audio = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = audio;
      const video = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      videoStreamRef.current = video;
      setFacingMode(mode);
      setCameraOpen(true);
      setTimeout(() => attachVideoStream(video), 50);
    } catch (e) {
      toast.error('Camera access denied');
    }
  };

  const switchCamera = async () => {
    if (switching) return;
    setSwitching(true);
    const newMode = facingMode === 'environment' ? 'user' : 'environment';
    try {
      const video = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: newMode, width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      // stop old camera video tracks (keep audio + keep recording via canvas pipeline)
      if (videoStreamRef.current) videoStreamRef.current.getTracks().forEach((t) => t.stop());
      videoStreamRef.current = video;
      setFacingMode(newMode);
      attachVideoStream(video);
    } catch (e) {
      toast.error('Could not switch camera');
    } finally {
      setSwitching(false);
    }
  };

  const stopCameraTracks = () => {
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (videoStreamRef.current) {
      videoStreamRef.current.getTracks().forEach((t) => t.stop());
      videoStreamRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((t) => t.stop());
      audioStreamRef.current = null;
    }
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
  };

  const closeCamera = () => {
    if (videoRecording && videoRecorderRef.current) {
      try { videoRecorderRef.current.stop(); } catch (_) { /* ignore */ }
    }
    stopCameraTracks();
    setVideoRecording(false);
    setElapsed(0);
    setCameraOpen(false);
  };

  const capturePhoto = () => {
    const v = videoPreviewRef.current;
    if (!v || !v.videoWidth) { toast.error('Camera not ready'); return; }
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext('2d').drawImage(v, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) { toast.error('Failed to capture photo'); return; }
      const file = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setFiles((prev) => [...prev, { file, type: 'image' }]);
      toast.success('Photo added to report');
    }, 'image/jpeg', 0.9);
  };

  const startVideoRecording = () => {
    if (!videoStreamRef.current) { toast.error('Camera not ready'); return; }
    const mimeType = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4']
      .find((m) => (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m))) || 'video/webm';

    // Canvas pipeline so the user can flip cameras mid-recording without interrupting the clip.
    const canvas = document.createElement('canvas');
    canvas.width = 1280; canvas.height = 720;
    canvasRef.current = canvas;
    const ctx = canvas.getContext('2d');
    const drawFrame = () => {
      const v = videoPreviewRef.current;
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      if (v && v.videoWidth) {
        const scale = Math.min(canvas.width / v.videoWidth, canvas.height / v.videoHeight);
        const w = v.videoWidth * scale, h = v.videoHeight * scale;
        ctx.drawImage(v, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
      }
      rafRef.current = requestAnimationFrame(drawFrame);
    };
    drawFrame();

    const canvasStream = canvas.captureStream(30);
    const tracks = [...canvasStream.getVideoTracks()];
    if (audioStreamRef.current) tracks.push(...audioStreamRef.current.getAudioTracks());
    const recordStream = new MediaStream(tracks);

    const mr = new MediaRecorder(recordStream, { mimeType });
    videoChunksRef.current = [];
    mr.ondataavailable = (e) => { if (e.data.size > 0) videoChunksRef.current.push(e.data); };
    mr.onstop = () => {
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
      const blob = new Blob(videoChunksRef.current, { type: mimeType });
      if (blob.size > MAX_VIDEO_BYTES) {
        toast.error('Recorded video exceeds the 500 MB limit — record a shorter clip');
      } else if (blob.size > 0) {
        const file = new File([blob], `camera-video-${Date.now()}.${ext}`, { type: blob.type });
        setFiles((prev) => [...prev, { file, type: 'video' }]);
        toast.success('Video added to report');
      }
    };
    videoRecorderRef.current = mr;
    mr.start();
    setVideoRecording(true);
    setElapsed(0);
    elapsedTimerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
  };

  const stopVideoRecording = () => {
    if (videoRecorderRef.current && videoRecording) {
      try { videoRecorderRef.current.stop(); } catch (_) { /* ignore */ }
    }
    setVideoRecording(false);
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    closeCamera();
  };

  const resetForm = () => {
    setText(''); setVisibility('management'); setFiles([]);
    setReportDate(new Date().toISOString().split('T')[0]);
    if (recording) stopRecording();
    closeCamera();
  };

  const handleSubmit = async () => {
    if (!text.trim() && files.length === 0) {
      toast.error('Add a message or at least one attachment');
      return;
    }
    setSubmitting(true);
    try {
      const attachments = [];
      for (const item of files) {
        const fd = new FormData();
        fd.append('file', item.file);
        const up = await axios.post(`${API}/files/upload?category=report`, fd, {
          headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'multipart/form-data' }
        });
        attachments.push({
          file_id: up.data.id,
          url: up.data.download_url,
          type: item.type,
          filename: up.data.filename,
          size: up.data.size,
          content_type: up.data.content_type
        });
      }
      await axios.post(`${API}/farm-operations-reports`, {
        date: reportDate, text: text.trim() || null, attachments, visibility
      }, authHeader());
      toast.success('Report submitted');
      setDialogOpen(false);
      resetForm();
      loadReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (reportId) => {
    const txt = (replyText[reportId] || '').trim();
    if (!txt) { toast.error('Enter a response'); return; }
    setReplyingId(reportId);
    try {
      await axios.post(`${API}/farm-operations-reports/${reportId}/responses`, { text: txt }, authHeader());
      setReplyText((p) => ({ ...p, [reportId]: '' }));
      toast.success('Response sent');
      loadReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send response');
    } finally {
      setReplyingId(null);
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Delete this report?')) return;
    try {
      await axios.delete(`${API}/farm-operations-reports/${reportId}`, authHeader());
      toast.success('Report deleted');
      loadReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const srcFor = (att) => `${BACKEND_URL}${att.url}?auth=${getToken()}`;

  const filtered = reports.filter((r) => {
    if (!textSearch.trim()) return true;
    const q = textSearch.toLowerCase();
    return (r.text || '').toLowerCase().includes(q) || (r.sender_name || '').toLowerCase().includes(q);
  });

  const canReply = (r) => isMgmt || r.sender_id === user.id;

  // Fetch a media URL and return a compressed JPEG data URL + dimensions for embedding in the PDF.
  const MAX_THUMB_PX = 320;
  const THUMB_QUALITY = 0.6;

  const blobFromUrl = async (url) => {
    const res = await fetch(url);
    if (!res.ok) throw new Error('fetch failed');
    return res.blob();
  };

  const drawToJpeg = (source, natW, natH) => {
    const scale = Math.min(1, MAX_THUMB_PX / Math.max(natW, natH));
    const w = Math.max(1, Math.round(natW * scale));
    const h = Math.max(1, Math.round(natH * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(source, 0, 0, w, h);
    return { dataUrl: canvas.toDataURL('image/jpeg', THUMB_QUALITY), w, h, canvas, ctx };
  };

  const imageThumb = async (url) => {
    const blob = await blobFromUrl(url);
    const objUrl = URL.createObjectURL(blob);
    try {
      const img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = objUrl;
      });
      return drawToJpeg(img, img.naturalWidth || img.width, img.naturalHeight || img.height);
    } finally { URL.revokeObjectURL(objUrl); }
  };

  const videoThumb = async (url) => {
    const blob = await blobFromUrl(url);
    const objUrl = URL.createObjectURL(blob);
    try {
      const video = document.createElement('video');
      video.muted = true; video.playsInline = true; video.preload = 'metadata';
      video.src = objUrl;
      await new Promise((resolve, reject) => {
        video.onloadeddata = resolve;
        video.onerror = reject;
        setTimeout(reject, 8000);
      });
      try {
        await new Promise((resolve) => { video.onseeked = resolve; video.currentTime = Math.min(0.1, (video.duration || 1) / 2); setTimeout(resolve, 2000); });
      } catch (_) { /* ignore seek issues */ }
      const { dataUrl, w, h, canvas, ctx } = drawToJpeg(video, video.videoWidth || 320, video.videoHeight || 180);
      // play-icon overlay
      const r = Math.max(14, Math.min(w, h) * 0.18);
      ctx.fillStyle = 'rgba(0,0,0,0.45)';
      ctx.beginPath(); ctx.arc(w / 2, h / 2, r, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(w / 2 - r * 0.35, h / 2 - r * 0.5);
      ctx.lineTo(w / 2 - r * 0.35, h / 2 + r * 0.5);
      ctx.lineTo(w / 2 + r * 0.55, h / 2);
      ctx.closePath(); ctx.fill();
      return { dataUrl: canvas.toDataURL('image/jpeg', THUMB_QUALITY), w, h };
    } finally { URL.revokeObjectURL(objUrl); }
  };

  const exportPDF = async () => {
    if (filtered.length === 0) { toast.error('No reports to export'); return; }
    setExporting(true);
    try {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const marginX = 40;
    const maxW = pageW - marginX * 2;
    let y = 50;

    const ensureSpace = (needed) => {
      if (y + needed > pageH - 40) { doc.addPage(); y = 50; }
    };
    const writeWrapped = (txt, { size = 10, style = 'normal', color = [51, 65, 85], indent = 0, lineGap = 14 } = {}) => {
      doc.setFont('helvetica', style);
      doc.setFontSize(size);
      doc.setTextColor(...color);
      const lines = doc.splitTextToSize(String(txt), maxW - indent);
      for (const ln of lines) {
        ensureSpace(lineGap);
        doc.text(ln, marginX + indent, y);
        y += lineGap;
      }
    };
    const writeLink = (label, url, indent = 12) => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(37, 99, 235);
      const lines = doc.splitTextToSize(label, maxW - indent);
      for (const ln of lines) {
        ensureSpace(12);
        doc.textWithLink(ln, marginX + indent, y, { url });
        y += 12;
      }
    };
    const PDF_THUMB_W = 150; // pt
    const drawThumb = (thumb, url, caption) => {
      const w = PDF_THUMB_W;
      const h = Math.max(1, Math.round(w * (thumb.h / thumb.w)));
      ensureSpace(h + 16);
      const x = marginX + 12;
      try { doc.addImage(thumb.dataUrl, 'JPEG', x, y, w, h); } catch (_) { /* ignore */ }
      doc.setDrawColor(203, 213, 225);
      doc.rect(x, y, w, h);
      // clickable overlay on the image
      doc.link(x, y, w, h, { url });
      y += h + 3;
      writeLink(caption, url);
    };

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(15, 23, 42);
    doc.text('Daily Farm Operations Report', marginX, y);
    y += 20;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    const scopeParts = [];
    if (singleDate) scopeParts.push(`Date: ${singleDate}`);
    else if (startDate || endDate) scopeParts.push(`Range: ${startDate || '…'} to ${endDate || '…'}`);
    if (textSearch) scopeParts.push(`Search: "${textSearch}"`);
    scopeParts.push(`${filtered.length} report(s)`);
    scopeParts.push(`Exported ${new Date().toLocaleString('en-GB', { timeZone: 'Africa/Lagos', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}`);
    doc.text(scopeParts.join('   ·   '), marginX, y);
    y += 10;
    doc.setDrawColor(203, 213, 225);
    ensureSpace(10); doc.line(marginX, y, pageW - marginX, y); y += 18;

    for (let idx = 0; idx < filtered.length; idx++) {
      const r = filtered[idx];
      ensureSpace(40);
      const vis = r.visibility === 'management' ? 'Management only' : r.visibility === 'management_distributor' ? 'Management & Distributors' : 'All staff';
      writeWrapped(`${r.sender_name}  ·  ${fmtDate(r.date)}  ·  ${vis}`, { size: 11, style: 'bold', color: [15, 23, 42], lineGap: 15 });
      if (r.created_at) writeWrapped(`Sent: ${fmtTime(r.created_at)}`, { size: 8, color: [100, 116, 139], lineGap: 11 });
      if (r.text) writeWrapped(r.text, { size: 10, color: [51, 65, 85] });

      if (r.attachments && r.attachments.length > 0) {
        writeWrapped('Attachments:', { size: 9, style: 'bold', color: [100, 116, 139], lineGap: 12 });
        for (const att of r.attachments) {
          const url = srcFor(att);
          const caption = `• ${att.filename || att.type} (${att.type})`;
          try {
            if (att.type === 'image') {
              const t = await imageThumb(url);
              drawThumb(t, url, caption);
            } else if (att.type === 'video') {
              const t = await videoThumb(url);
              drawThumb(t, url, caption);
            } else {
              writeLink(caption, url);
            }
          } catch (_) {
            writeLink(caption, url);
          }
        }
      }

      if (r.responses && r.responses.length > 0) {
        writeWrapped('Responses:', { size: 9, style: 'bold', color: [100, 116, 139], lineGap: 12 });
        r.responses.forEach((resp) => {
          const mgmt = ['admin', 'manager'].includes(resp.author_role);
          writeWrapped(`${resp.author_name}${mgmt ? ' (Management)' : ''} · ${fmtTime(resp.created_at)}`, { size: 8, style: 'bold', color: mgmt ? [5, 150, 105] : [100, 116, 139], indent: 12, lineGap: 11 });
          writeWrapped(resp.text, { size: 9, color: [71, 85, 105], indent: 12, lineGap: 12 });
        });
      }

      y += 8;
      if (idx < filtered.length - 1) {
        ensureSpace(10);
        doc.setDrawColor(226, 232, 240);
        doc.line(marginX, y, pageW - marginX, y);
        y += 16;
      }
    }

    const fname = singleDate
      ? `farm-operations-report-${singleDate}.pdf`
      : `farm-operations-reports-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fname);
    toast.success('Report exported to PDF');
    } catch (e) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="farm-ops-report-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
            <ClipboardList className="w-6 h-6 text-emerald-700" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Daily Farm Operations Report</h1>
            <p className="text-sm text-slate-500">Share text, audio and video updates with the team or management.</p>
          </div>
        </div>
        {isSender && (
          <Button onClick={() => setDialogOpen(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-report-btn">
            <Plus className="w-4 h-4 mr-1" /> New Report
          </Button>
        )}
        {isMgmt && (
          <Button onClick={exportPDF} disabled={exporting} variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" data-testid="export-pdf-btn">
            {exporting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Generating...</> : <><FileDown className="w-4 h-4 mr-1" /> Export PDF</>}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-xl p-4 mb-5 flex flex-wrap items-end gap-3" data-testid="report-filters">
        <div>
          <Label className="text-xs text-slate-500">On date</Label>
          <Input type="date" value={singleDate} onChange={(e) => { setSingleDate(e.target.value); setStartDate(''); setEndDate(''); }} className="h-9 w-44" data-testid="filter-single-date" />
        </div>
        <span className="text-slate-300 pb-2">or</span>
        <div>
          <Label className="text-xs text-slate-500">From</Label>
          <Input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setSingleDate(''); }} className="h-9 w-40" data-testid="filter-start-date" />
        </div>
        <div>
          <Label className="text-xs text-slate-500">To</Label>
          <Input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setSingleDate(''); }} className="h-9 w-40" data-testid="filter-end-date" />
        </div>
        <div className="flex-1 min-w-[160px]">
          <Label className="text-xs text-slate-500">Search</Label>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <Input value={textSearch} onChange={(e) => setTextSearch(e.target.value)} placeholder="Search text or sender" className="h-9 pl-8" data-testid="filter-search" />
          </div>
        </div>
        {(singleDate || startDate || endDate || textSearch) && (
          <Button variant="ghost" size="sm" onClick={() => { setSingleDate(''); setStartDate(''); setEndDate(''); setTextSearch(''); }} data-testid="clear-filters-btn">
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      {/* Reports list */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading reports...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="no-reports-msg">
          <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-40" />
          No reports found.
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((r) => (
            <div key={r.id} className="bg-white border rounded-xl p-4 shadow-sm" data-testid={`report-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-800">{r.sender_name}</span>
                  <span className="text-xs text-slate-400">· {fmtDate(r.date)}</span>
                  {r.created_at && (
                    <span className="text-xs text-slate-500 font-medium" data-testid={`report-timestamp-${r.id}`}>· Sent {fmtTime(r.created_at)}</span>
                  )}
                  {r.visibility === 'management' ? (
                    <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100"><Lock className="w-3 h-3 mr-1" /> Management only</Badge>
                  ) : r.visibility === 'management_distributor' ? (
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><Truck className="w-3 h-3 mr-1" /> Management & Distributors</Badge>
                  ) : (
                    <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100"><Users className="w-3 h-3 mr-1" /> All staff</Badge>
                  )}
                </div>
                {(role === 'admin' || r.sender_id === user.id) && (
                  <Button variant="ghost" size="sm" className="text-slate-400 hover:text-rose-600" onClick={() => handleDelete(r.id)} data-testid={`delete-report-${r.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {r.text && <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{r.text}</p>}

              {/* Attachments */}
              {r.attachments && r.attachments.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  {r.attachments.map((att, i) => (
                    <div key={i} className="border rounded-lg p-2 bg-slate-50" data-testid={`attachment-${r.id}-${i}`}>
                      {att.type === 'video' && (
                        <video src={srcFor(att)} controls className="w-full rounded max-h-64" preload="metadata" />
                      )}
                      {att.type === 'audio' && (
                        <audio src={srcFor(att)} controls className="w-full" preload="metadata" />
                      )}
                      {att.type === 'image' && (
                        <a href={srcFor(att)} target="_blank" rel="noreferrer">
                          <img src={srcFor(att)} alt={att.filename} className="w-full rounded max-h-64 object-cover" />
                        </a>
                      )}
                      <div className="flex items-center gap-1 text-xs text-slate-500 mt-1 truncate">
                        {att.type === 'video' && <FileVideo className="w-3 h-3" />}
                        {att.type === 'audio' && <FileAudio className="w-3 h-3" />}
                        {att.type === 'image' && <ImageIcon className="w-3 h-3" />}
                        <span className="truncate">{att.filename}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Responses thread */}
              {r.responses && r.responses.length > 0 && (
                <div className="mt-3 space-y-2 border-l-2 border-slate-100 pl-3" data-testid={`responses-${r.id}`}>
                  {r.responses.map((resp) => {
                    const mgmt = ['admin', 'manager'].includes(resp.author_role);
                    return (
                      <div key={resp.id} className={`rounded-lg p-2 text-sm ${mgmt ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mb-0.5">
                          <span className="font-medium text-slate-700">{resp.author_name}</span>
                          {mgmt && <Badge className="bg-emerald-600 text-white hover:bg-emerald-600 text-[10px] px-1 py-0">Management</Badge>}
                          <span>· {fmtTime(resp.created_at)}</span>
                        </div>
                        <p className="text-slate-700 whitespace-pre-wrap">{resp.text}</p>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Reply box */}
              {canReply(r) && (
                <div className="flex items-start gap-2 mt-3">
                  <Textarea
                    value={replyText[r.id] || ''}
                    onChange={(e) => setReplyText((p) => ({ ...p, [r.id]: e.target.value }))}
                    placeholder={isMgmt ? 'Respond to this report...' : 'Reply...'}
                    className="min-h-[38px] text-sm"
                    rows={1}
                    data-testid={`reply-input-${r.id}`}
                  />
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" size="sm" className="shrink-0 px-2" title="Add emoji" data-testid={`reply-emoji-btn-${r.id}`}>
                        <Smile className="w-4 h-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-auto border-0 shadow-lg" align="end" data-testid={`reply-emoji-picker-${r.id}`}>
                      <EmojiPicker
                        onEmojiClick={(emojiData) => setReplyText((p) => ({ ...p, [r.id]: `${p[r.id] || ''}${emojiData.emoji}` }))}
                        width={300}
                        height={350}
                        searchPlaceholder="Search emoji"
                        previewConfig={{ showPreview: false }}
                      />
                    </PopoverContent>
                  </Popover>
                  <Button size="sm" onClick={() => handleReply(r.id)} disabled={replyingId === r.id} className="bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid={`reply-btn-${r.id}`}>
                    {replyingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Reply className="w-4 h-4" />}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* New Report Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-lg" data-testid="new-report-dialog">
          <DialogHeader>
            <DialogTitle>New Daily Farm Operations Report</DialogTitle>
            <DialogDescription>Add a message and optionally attach audio, video, or photos.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Report Date</Label>
              <Input type="date" value={reportDate} onChange={(e) => setReportDate(e.target.value)} className="mt-1" data-testid="report-date-input" />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Message</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" size="sm" className="gap-1.5 h-8" data-testid="report-emoji-btn">
                      <Smile className="w-4 h-4" /> Add emoji
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 w-auto border-0 shadow-lg" align="end" data-testid="report-emoji-picker">
                    <EmojiPicker
                      onEmojiClick={(emojiData) => setText((prev) => `${prev}${emojiData.emoji}`)}
                      width={300}
                      height={350}
                      searchPlaceholder="Search emoji"
                      previewConfig={{ showPreview: false }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="What happened on the farm today?" className="mt-1 min-h-[90px]" data-testid="report-text-input" />
            </div>

            <div>
              <Label>Send to</Label>
              <RadioGroup value={visibility} onValueChange={setVisibility} className="mt-2 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="visibility-all">
                  <RadioGroupItem value="all" /> <Users className="w-4 h-4 text-sky-600" /> All staff and management
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="visibility-management">
                  <RadioGroupItem value="management" /> <Lock className="w-4 h-4 text-rose-600" /> Management only (confidential)
                </label>
                <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="visibility-management-distributor">
                  <RadioGroupItem value="management_distributor" /> <Truck className="w-4 h-4 text-amber-600" /> Management and Distributors only
                </label>
              </RadioGroup>
            </div>

            <div>
              <Label>Attachments <span className="text-xs text-slate-400">(videos max 500 MB each)</span></Label>
              <div className="flex items-center gap-2 mt-1">
                <input ref={fileInputRef} type="file" multiple accept="image/*,video/*,audio/*" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} data-testid="file-input" />
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="attach-btn">
                  <Paperclip className="w-4 h-4 mr-1" /> Add files
                </Button>
                {!recording ? (
                  <Button type="button" variant="outline" size="sm" onClick={startRecording} data-testid="record-btn">
                    <Mic className="w-4 h-4 mr-1" /> Record voice
                  </Button>
                ) : (
                  <Button type="button" variant="destructive" size="sm" onClick={stopRecording} data-testid="stop-record-btn">
                    <Square className="w-4 h-4 mr-1" /> Stop ({'recording...'})
                  </Button>
                )}
                {!cameraOpen && (
                  <Button type="button" variant="outline" size="sm" onClick={openCamera} data-testid="record-video-btn">
                    <Video className="w-4 h-4 mr-1" /> Record video
                  </Button>
                )}
              </div>

              {cameraOpen && (
                <div className="mt-3 border rounded-lg p-3 bg-slate-900" data-testid="camera-panel">
                  <div className="relative">
                    <video ref={videoPreviewRef} muted playsInline className="w-full rounded max-h-72 bg-black" data-testid="camera-preview" />
                    {videoRecording && (
                      <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                        <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" />
                        REC {String(Math.floor(elapsed / 60)).padStart(2, '0')}:{String(elapsed % 60).padStart(2, '0')}
                      </div>
                    )}
                    <div className="absolute top-2 right-2 flex gap-2">
                      <button type="button" onClick={switchCamera} disabled={switching} title="Flip camera"
                        className="w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center disabled:opacity-50" data-testid="flip-camera-btn">
                        {switching ? <Loader2 className="w-4 h-4 animate-spin" /> : <SwitchCamera className="w-4 h-4" />}
                      </button>
                      <button type="button" onClick={capturePhoto} title="Take photo"
                        className="w-9 h-9 rounded-full bg-white/90 hover:bg-white text-slate-800 flex items-center justify-center" data-testid="capture-photo-btn">
                        <Camera className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-2 mt-3">
                    {!videoRecording ? (
                      <>
                        <Button type="button" size="sm" onClick={startVideoRecording} className="bg-rose-600 hover:bg-rose-700" data-testid="start-video-btn">
                          <Video className="w-4 h-4 mr-1" /> Start recording
                        </Button>
                        <Button type="button" size="sm" onClick={capturePhoto} className="bg-sky-600 hover:bg-sky-700" data-testid="photo-btn">
                          <Camera className="w-4 h-4 mr-1" /> Take photo
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={closeCamera} className="bg-white" data-testid="close-camera-btn">
                          <X className="w-4 h-4 mr-1" /> Close camera
                        </Button>
                      </>
                    ) : (
                      <Button type="button" size="sm" variant="destructive" onClick={stopVideoRecording} data-testid="stop-video-btn">
                        <Square className="w-4 h-4 mr-1" /> Stop & attach
                      </Button>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-300 text-center mt-2">Keep clips under 500 MB. The recording is attached to this report when you stop.</p>
                </div>
              )}
              {files.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="selected-files">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1">
                      <span className="flex items-center gap-1 truncate">
                        {f.type === 'video' && <FileVideo className="w-3 h-3" />}
                        {f.type === 'audio' && <FileAudio className="w-3 h-3" />}
                        {f.type === 'image' && <ImageIcon className="w-3 h-3" />}
                        <span className="truncate">{f.file.name}</span>
                        <span className="text-slate-400">· {fmtSize(f.file.size)}</span>
                      </span>
                      <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-rose-600" data-testid={`remove-file-${i}`}><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-report-btn">
              {submitting ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-1" /> Send Report</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DailyFarmOperationsReport;
